import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || '/contact.php';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    service_interests: [], // Changed to array for multiple services
    description: '',
    privacy_consent: false,
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Sandėliavimas',
    'Krovos darbai',
    'Prekių paruošimas',
    'Užsakymų valdymas',
    'Apskaita ir kontrolė',
    'Logistikos organizavimas',
    '3PL logistika',
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      service_interests: prev.service_interests.includes(service)
        ? prev.service_interests.filter(s => s !== service)
        : [...prev.service_interests, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Convert array to comma-separated string for backend
      const submitData = {
        ...formData,
        service_interest: formData.service_interests.join(', ') || 'Nenurodyta'
      };

      const response = await axios.post(CONTACT_ENDPOINT, submitData, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: response.data.message
        });
        // Reset form
        setFormData({
          company_name: '',
          contact_person: '',
          email: '',
          phone: '',
          service_interests: [],
          description: '',
          privacy_consent: false,
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Įvyko klaida. Bandykite dar kartą.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8" data-testid="contact-form">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Name */}
        <div>
          <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
            Įmonės pavadinimas *
          </label>
          <input
            type="text"
            id="company_name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="UAB Pavyzdys"
            data-testid="input-company-name"
          />
        </div>

        {/* Contact Person */}
        <div>
          <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-2">
            Kontaktinis asmuo *
          </label>
          <input
            type="text"
            id="contact_person"
            name="contact_person"
            value={formData.contact_person}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="Vardas Pavardė"
            data-testid="input-contact-person"
          />
        </div>

        {/* Email and Phone in Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              El. paštas *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="info@imone.lt"
              data-testid="input-email"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Telefonas
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="+370 600 00000"
              data-testid="input-phone"
            />
          </div>
        </div>

        {/* Service Interests - Checkboxes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dominančios paslaugos
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {serviceOptions.map((service, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.service_interests.includes(service)}
                  onChange={() => handleServiceChange(service)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  data-testid={`checkbox-service-${index}`}
                />
                <span className="text-sm text-gray-700">{service}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Aprašymas
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            placeholder="Aprašykite savo poreikius: palečių skaičius, SKU, užsakymų kiekiai ir kt."
            data-testid="textarea-description"
          ></textarea>
        </div>

        {/* Privacy Consent */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="privacy_consent"
            name="privacy_consent"
            checked={formData.privacy_consent}
            onChange={handleChange}
            required
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            data-testid="checkbox-privacy"
          />
          <label htmlFor="privacy_consent" className="ml-2 text-sm text-gray-600">
            Sutinku su{' '}
            <a href="/privatumo-politika" className="text-blue-600 hover:underline">
              privatumo politika
            </a>{' '}
            *
          </label>
        </div>

        {/* Status Message */}
        {status.message && (
          <div
            className={`flex items-center space-x-2 p-4 rounded-lg ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
            data-testid="form-status-message"
          >
            {status.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>{status.message}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          data-testid="submit-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Siunčiama...</span>
            </>
          ) : (
            <span>Siųsti užklausą</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
